import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Send, Loader2, Sparkles, ArrowRight, ShieldAlert } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { notifyRemembered } from "@/lib/memoryToast";
import { useAuth } from "@/context/AuthContext";
import { Wordmark } from "@/components/Brand";
import { SargeMarkdown } from "@/components/SargeMarkdown";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

function Bubble({ m }) {
  const isUser = m.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid={`intake-msg-${m.role}`}>
      <div className={`max-w-[88%] sm:max-w-[80%] px-4 py-3 border ${isUser ? "bg-wr-surface2 border-wr-border text-white" : "bg-wr-surface border-l-2 border-l-wr-red border-y-wr-border border-r-wr-border"}`}>
        {!isUser && <span className="wr-label !text-wr-red block mb-2">▸ SARGE</span>}
        {isUser ? <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{m.content}</p>
                : <SargeMarkdown>{m.content}</SargeMarkdown>}
      </div>
    </div>
  );
}

export default function Intake() {
  const { loadMe } = useAuth();
  const navigate = useNavigate();
  const [convId, setConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState({ completeness: 0, missing_areas: [] });
  const [finishing, setFinishing] = useState(false);
  const scrollRef = useRef(null);

  const refreshStatus = useCallback(async () => {
    try { const { data } = await api.get("/intake/status"); setStatus(data); } catch (e) {}
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.post("/intake/start", {});
        setConvId(data.conversation_id);
        const conv = await api.get(`/conversations/${data.conversation_id}`);
        setMessages(conv.data.messages);
        refreshStatus();
      } catch (e) { toast.error(apiError(e)); }
    })();
  }, [refreshStatus]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async () => {
    const content = input.trim();
    if (!content || sending || !convId) return;
    setInput(""); setSending(true);
    setMessages((m) => [...m, { id: `t-${Date.now()}`, role: "user", content }]);
    try {
      const { data } = await api.post(`/conversations/${convId}/messages`, { content });
      setMessages((m) => [...m.filter((x) => !String(x.id).startsWith("t-")), data.user_message, data.assistant_message]);
      // Persistence succeeded — subtle, non-blocking confirmation (no extra model call).
      notifyRemembered(data.remembered, navigate);
      refreshStatus();
    } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };

  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  const finish = async () => {
    setFinishing(true);
    try {
      const { data } = await api.post("/intake/operating-read", {});
      setMessages((m) => [...m, { id: `read-${Date.now()}`, role: "assistant", content: data.operating_read }]);
      await loadMe();
      toast.success("Initial Operating Read ready. Your first Command Brief is set.");
      setTimeout(() => navigate("/app/brief"), 1400);
    } catch (e) { toast.error(apiError(e)); setFinishing(false); }
  };

  return (
    <div className="min-h-[100dvh] bg-wr-bg flex flex-col">
      <header className="border-b border-wr-border bg-black/80 backdrop-blur-xl safe-top sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Wordmark size="sm" />
          <button onClick={() => navigate("/app/brief")} data-testid="intake-skip-app"
            className="wr-label hover:text-white flex items-center gap-1.5">
            Skip for now <ArrowRight size={13} />
          </button>
        </div>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 pb-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="wr-label">Getting to know your business</span>
            <span className="wr-mono text-xs text-wr-sub" data-testid="intake-completeness">{status.completeness}%</span>
          </div>
          <div className="h-1.5 bg-wr-surface2 overflow-hidden">
            <div className="h-full bg-wr-red transition-all duration-500" style={{ width: `${status.completeness}%` }} />
          </div>
        </div>
      </header>

      <main ref={scrollRef} className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-5 space-y-4" data-testid="intake-messages">
          {messages.map((m) => <Bubble key={m.id} m={m} />)}
          {sending && (
            <div className="flex justify-start" data-testid="intake-thinking">
              <div className="px-4 py-3 border border-l-2 border-l-wr-red border-wr-border bg-wr-surface flex items-center gap-2">
                <Loader2 size={14} className="animate-spin text-wr-red" /><span className="wr-label">Sarge is listening…</span>
              </div>
            </div>
          )}
          {status.completeness >= 50 && !finishing && (
            <div className="border border-wr-red/40 bg-wr-redMuted p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-testid="intake-finish-card">
              <p className="text-sm text-white flex items-center gap-2"><Sparkles size={15} className="text-wr-red" /> Enough to work with. Want your Initial Operating Read?</p>
              <Button onClick={finish} data-testid="intake-finish" className="rounded-none bg-wr-red hover:bg-wr-redHover text-white wr-mono uppercase tracking-wider text-xs h-10">
                Get Operating Read
              </Button>
            </div>
          )}
          {finishing && (
            <div className="flex justify-start"><div className="px-4 py-3 border border-l-2 border-l-wr-red border-wr-border bg-wr-surface flex items-center gap-2">
              <Loader2 size={14} className="animate-spin text-wr-red" /><span className="wr-label">Preparing your operating read…</span></div></div>
          )}
        </div>
      </main>

      <div className="border-t border-wr-border bg-black/95 backdrop-blur-xl safe-bottom sticky bottom-0">
        <div className="max-w-3xl mx-auto px-3 sm:px-6 py-3">
          <div className="flex items-center gap-1.5 mb-2 text-wr-muted">
            <ShieldAlert size={12} /><span className="text-[0.66rem]">Never share passwords, API keys, banking details, or any secret.</span>
          </div>
          <div className="flex items-end gap-2">
            <Textarea data-testid="intake-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKey}
              placeholder="Answer naturally — or type 'skip'…" rows={1}
              className="flex-1 bg-wr-bg border-wr-border rounded-none focus-visible:ring-0 resize-none max-h-40 min-h-[44px]" />
            <Button onClick={send} disabled={sending || !input.trim()} data-testid="intake-send"
              className="rounded-none bg-wr-red hover:bg-wr-redHover text-white h-11 w-11 p-0 shrink-0"><Send size={17} /></Button>
          </div>
        </div>
      </div>
    </div>
  );
}
